import { useCallback, useState } from 'react'
import type { InputEvent } from 'react'
import PhoneInput, {
  isPossiblePhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input'
import Tippy from '@tippyjs/react'

import { CountryCode } from 'libphonenumber-js/types'
import Icon from '../../ui/Icon'

import 'react-phone-number-input/style.css'

interface PhoneNumberInputProps {
  id?: string
  name: string
  value: string
  defaultCountry?: CountryCode
  className?: string
  smartCaret?: boolean
  international?: boolean
  required?: boolean
  label?: string
  disabled?: boolean
  infoMessage?: string
  errorMessage?: string
  onChange: (name: string, value: string, isValid: boolean) => void
  onValidate?: (value: string) => boolean
}

export const PhoneNumberInput = ({
  id,
  name,
  value,
  defaultCountry = 'RO',
  className = '',
  smartCaret = true,
  international = true,
  required = false,
  label,
  disabled = false,
  infoMessage,
  errorMessage,
  onChange,
  onValidate,
}: PhoneNumberInputProps) => {
  const [isError, setIsError] = useState(false)
  const [isTouch, setIsTouch] = useState(false)
  const [isFocus, setIsFocus] = useState(false)

  const isRequired = isTouch && required && !value.trim()

  const handleChange = useCallback(
    (currentValue: string = '') => {
      setIsTouch(true)

      const hasValue = currentValue.trim().length > 0
      const phoneValid = hasValue
        ? isPossiblePhoneNumber(currentValue)
        : !required
      const customValid = onValidate ? onValidate(currentValue) : true
      const valid = phoneValid && customValid

      setIsError(hasValue && !valid)
      onChange(name, currentValue, valid)
    },
    [name, onChange, onValidate, required]
  )

  const handleInput = useCallback(
    (event: InputEvent<HTMLInputElement>) => {
      const phoneNumber = parsePhoneNumber(
        event.currentTarget.value,
        defaultCountry
      )

      if (!phoneNumber?.isPossible() || phoneNumber.number === value) {
        return
      }

      queueMicrotask(() => handleChange(phoneNumber.number))
    },
    [defaultCountry, handleChange, value]
  )

  const fieldClass = [
    'field',
    className,
    required && 'required',
    !isFocus && isRequired && 'is-required',
    !isFocus && isError && 'is-error',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={fieldClass}>
      {label && <label htmlFor={id}>{label}</label>}

      <div className="input-icon-wrap">
        <PhoneInput
          id={id}
          className={`input phone-number-input ${disabled ? 'disabled' : ''}`}
          name={name}
          defaultCountry={defaultCountry}
          smartCaret={smartCaret}
          international={international}
          placeholder={disabled ? '' : undefined}
          required={required}
          disabled={disabled}
          value={value}
          numberInputProps={{ onInput: handleInput }}
          onChange={handleChange}
          onFocus={() => setIsFocus(true)}
          onBlur={() => setIsFocus(false)}
        />

        {infoMessage && (
          <Tippy content={infoMessage} placement="top" className="ph-tooltip">
            <div className="input-icon-toggle">
              <Icon name="info" size={16} />
            </div>
          </Tippy>
        )}
      </div>

      {errorMessage && !isFocus && (isRequired || isError) && (
        <div className="field-msg err" role="alert">
          <span className="ico">
            <Icon name="warn" size={13} />
          </span>

          {errorMessage}
        </div>
      )}
    </div>
  )
}
